<template>
  <div class="project-jobs-panel" data-test="project-jobs-panel">
    <div
      class="d-flex flex-wrap align-items-start justify-content-between mb-4"
    >
      <div class="mb-2">
        <h3 class="font-weight-bold mb-1">Jobs</h3>
        <p class="text-muted mb-0">
          Jobs advertised for this project and who has applied.
        </p>
      </div>
      <b-button
        v-if="canCreate"
        variant="primary"
        class="post-project-job-btn"
        :to="{ name: 'jobs.new-page', query: { project_id: projectId } }"
      >
        Post a job
      </b-button>
    </div>

    <p v-if="loading" class="text-muted py-3 mb-0">Loading jobs...</p>

    <template v-else>
      <job-insights :tiles="tiles" />

      <div v-if="!hasJobs" class="jobs-empty" data-test="jobs-empty">
        <span class="jobs-empty__icon"><b-icon icon="briefcase" /></span>
        <div class="jobs-empty__title">No jobs for this project yet</div>
        <p class="jobs-empty__text">
          Post a job and tradies can find it and apply. Jobs you link to this
          project will show up here.
        </p>
        <b-button
          v-if="canCreate"
          variant="outline-primary"
          class="jobs-empty__btn"
          :to="{ name: 'jobs.new-page', query: { project_id: projectId } }"
        >
          Post the first job
        </b-button>
      </div>

      <template v-else>
        <section
          v-for="group in visibleGroups"
          :key="group.key"
          class="job-group"
          :data-test="`jobs-${group.key}`"
        >
          <h5 class="job-group__title">
            <span
              class="job-group__dot"
              :class="`job-group__dot--${group.key}`"
            />
            {{ group.label }}
            <span class="job-group__count">{{ group.jobs.length }}</span>
          </h5>

          <div
            v-for="job in group.jobs"
            :key="job.id"
            class="project-job-row"
            role="button"
            tabindex="0"
            @click="goToJob(job)"
            @keyup.enter="goToJob(job)"
          >
            <div class="project-job-main">
              <div class="project-job-title">{{ job.title }}</div>
              <div class="project-job-meta">
                <span v-if="job.trade" class="project-job-chip">
                  {{ job.trade.name }}
                </span>
                <span>
                  <b-icon icon="people-fill" class="mr-1" />{{
                    job.applications_count || 0
                  }}
                  applicant{{ job.applications_count === 1 ? '' : 's' }}
                </span>
                <span>
                  <b-icon icon="calendar-event" class="mr-1" />{{
                    job.expires_at
                      ? `Closes ${formatDate(job.expires_at)}`
                      : 'No closing date'
                  }}
                </span>
              </div>
            </div>
            <job-status-badge :job="job" />
            <b-icon icon="chevron-right" class="project-job-chevron" />
          </div>
        </section>
      </template>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import JobInsights from '@/components/jobPostings/JobInsights.vue'
import JobStatusBadge from '@/components/jobPostings/JobStatusBadge.vue'
import apiToasts from '@/mixins/apiToasts'
import { formatDate } from '@/utils/jobPostings'

// Jobs linked to one project, grouped as live, expiring soon and expired. The
// API owns what "live" and "expired" mean; this only asks for each group.
export default {
  name: 'ProjectJobsPanel',

  components: { JobInsights, JobStatusBadge },

  mixins: [apiToasts],

  props: {
    projectId: { type: Number, required: true },
  },

  data() {
    return {
      loading: false,
      live: [],
      expiring: [],
      expired: [],
    }
  },

  computed: {
    ...mapGetters('auth', ['hasPermission']),

    canCreate() {
      return this.hasPermission('jobs.create')
    },

    tiles() {
      return [
        {
          key: 'live',
          label: 'Live jobs',
          value: this.live.length,
          icon: 'broadcast',
        },
        {
          key: 'expiring',
          label: 'Expiring in 7 days',
          value: this.expiring.length,
          icon: 'clock-history',
        },
        {
          key: 'expired',
          label: 'Expired',
          value: this.expired.length,
          icon: 'calendar-x',
        },
      ]
    },

    groups() {
      return [
        { key: 'live', label: 'Live', jobs: this.live },
        { key: 'expiring', label: 'Expiring soon', jobs: this.expiring },
        { key: 'expired', label: 'Expired', jobs: this.expired },
      ]
    },

    // A group with nothing in it is left out rather than shown as "none".
    visibleGroups() {
      return this.groups.filter((g) => g.jobs.length)
    },

    hasJobs() {
      return this.visibleGroups.length > 0
    },
  },

  watch: {
    projectId: 'load',
  },

  created() {
    this.load()
  },

  methods: {
    ...mapActions('jobPosting', ['fetchProjectJobs']),

    formatDate,

    async load() {
      this.loading = true
      try {
        const [live, expiring, expired] = await Promise.all([
          this.fetchProjectJobs({
            projectId: this.projectId,
            status: 'published',
          }),
          this.fetchProjectJobs({
            projectId: this.projectId,
            status: 'expiring',
          }),
          this.fetchProjectJobs({
            projectId: this.projectId,
            status: 'expired',
          }),
        ])
        this.live = live
        this.expiring = expiring
        this.expired = expired
      } catch (err) {
        this.onError(err, "Could not load this project's jobs.")
      } finally {
        this.loading = false
      }
    },

    goToJob(job) {
      this.$router.push({ name: 'jobs.view-job', params: { jobId: job.id } })
    },
  },
}
</script>

<style lang="scss" scoped>
// Gray values follow the newer Tailwind-style scale in DESIGN.md.
.project-jobs-panel {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0px 10px 25px #00429d0d;
}

.post-project-job-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  min-width: 140px;
  padding: 0 24px;
  font-weight: 600;
}

// Friendly empty state instead of three "none" lines.
.jobs-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2.5rem 1rem;
  border: 2px dashed #e5e7eb;
  border-radius: 12px;
  background: #f9fafb;
}

.jobs-empty__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: #e6f0ff;
  color: #006cff;
  font-size: 1.5rem;
}

.jobs-empty__title {
  margin-top: 1rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
}

.jobs-empty__text {
  max-width: 420px;
  margin: 0.35rem 0 1.1rem;
  color: #6b7280;
}

.jobs-empty__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 24px;
  font-weight: 600;
}

.job-group {
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
}

.job-group__title {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
}

.job-group__dot {
  width: 10px;
  height: 10px;
  margin-right: 0.5rem;
  border-radius: 50%;
  background: #9ca3af;

  &--live {
    background: #1e9e63;
  }

  &--expiring {
    background: #f59e0b;
  }

  &--expired {
    background: #9ca3af;
  }
}

.job-group__count {
  margin-left: 0.5rem;
  padding: 0.05rem 0.55rem;
  border-radius: 999px;
  background: #f3f4f6;
  font-size: 0.75rem;
  font-weight: 700;
  color: #6b7280;
}

.project-job-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.9rem 1.1rem;
  margin-bottom: 0.6rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover,
  &:focus {
    outline: none;
    border-color: #2c66d5;
    box-shadow: 0px 12px 20px #2c66d533;
  }
}

.project-job-main {
  flex: 1;
  min-width: 0;
}

.project-job-title {
  font-weight: 700;
  color: #111827;
  overflow-wrap: anywhere;
}

.project-job-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 1rem;
  margin-top: 0.35rem;
  font-size: 0.85rem;
  color: #6b7280;
}

.project-job-chip {
  padding: 0.15rem 0.65rem;
  border-radius: 999px;
  background-color: #e6f0ff;
  font-size: 0.75rem;
  font-weight: 600;
  color: #006cff;
}

.project-job-chevron {
  flex-shrink: 0;
  color: #d1d5db;
}

.project-job-row:hover .project-job-chevron {
  color: #006cff;
}
</style>
